import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../common/Button';
import type { AdminUserView } from '../../types/admin-user.types';
import type { AccountStatus } from '../../types/auth.types';

interface LockAccountConfirmModalProps {
  account: AdminUserView;
  targetStatus: AccountStatus;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

export function LockAccountConfirmModal({
  account,
  targetStatus,
  onClose,
  onConfirm,
}: LockAccountConfirmModalProps): React.ReactElement {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isLocking = targetStatus === 'LOCKED';
  const actionLabel = isLocking ? 'Lock Account' : 'Unlock Account';
  const description = isLocking
    ? 'The user will be immediately prevented from logging in or making any API calls. Their data will be preserved.'
    : 'The user will be able to log in again immediately.';
  const confirmBtnClass = isLocking
    ? 'bg-amber-600/20 hover:bg-amber-600/30 text-amber-400 border border-amber-500/30'
    : 'bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-400 border border-emerald-500/30';

  const handleConfirm = async () => {
    setError(null);
    setLoading(true);
    try {
      await onConfirm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Operation failed.');
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="lock-modal-title">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-sm glass-card p-6 animate-slide-in">
        <h2 id="lock-modal-title" className="text-lg font-bold text-white mb-2">{actionLabel}</h2>
        <p className="text-sm text-slate-400 mb-1">
          Account: <span className="text-white font-medium">{account.email}</span>
        </p>
        <p className="text-sm text-slate-400 mb-5">{description}</p>

        {error && (
          <div className="p-3 mb-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
            {error}
          </div>
        )}

        <div className="flex gap-3">
          <Button
            id="lock-modal-cancel-btn"
            type="button"
            variant="ghost"
            className="flex-1"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <button
            id="lock-modal-confirm-btn"
            type="button"
            disabled={loading}
            onClick={() => { void handleConfirm(); }}
            className={`flex-1 inline-flex items-center justify-center gap-2 font-medium text-sm px-4 py-2 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${confirmBtnClass}`}
          >
            {loading ? (
              <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : null}
            {actionLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
